import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import type { EmailTemplateResponse } from "../api/clientManagement";
import { SectionCard } from "./SectionCard";
import { SettingsSection } from "./SettingsSection";

interface EmailTemplatesTabProps {
  isCentralAdmin: boolean;
  selectedTemplateKey: string;
  onTemplateChange: (templateKey: string) => void;
  templateOptions: { value: string; label: string }[];
  templateQuery: UseQueryResult<EmailTemplateResponse>;
  updateTemplateMutation: UseMutationResult<
    EmailTemplateResponse,
    unknown,
    { name?: string; subject: string; html_body: string; text_body: string; is_active?: boolean }
  >;
}

const PREVIEW_VALUES = { firstName: "Alex", email: "alex@example.com" };

const applyPreview = (value: string) =>
  value
    .replace(/{{\s*firstName\s*}}/gi, PREVIEW_VALUES.firstName)
    .replace(/{{\s*email\s*}}/gi, PREVIEW_VALUES.email);

export const EmailTemplatesTab: React.FC<EmailTemplatesTabProps> = ({
  isCentralAdmin,
  selectedTemplateKey,
  onTemplateChange,
  templateOptions,
  templateQuery,
  updateTemplateMutation,
}) => {
  const template = templateQuery.data?.data;
  const [editorTab, setEditorTab] = useState<"edit" | "preview">("edit");
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [htmlBody, setHtmlBody] = useState("");
  const [textBody, setTextBody] = useState("");
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!template) return;
    setName(template.name || "");
    setSubject(template.subject || "");
    setHtmlBody(template.html_body || "");
    setTextBody(template.text_body || "");
    setIsActive(Boolean(template.is_active));
  }, [template]);

  useEffect(() => {
    updateTemplateMutation.reset();
    setEditorTab("edit");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplateKey]);

  if (!isCentralAdmin) {
    return (
      <SettingsSection>
        <Alert severity="warning">You do not have permission to manage email templates.</Alert>
      </SettingsSection>
    );
  }

  const canSave = subject.trim() && textBody.trim() && htmlBody.trim();

  return (
    <SettingsSection>
      {/* Template selector */}
      <Box>
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          Template
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {templateOptions.map((opt) => (
            <Chip
              key={opt.value}
              label={opt.label}
              icon={<MailOutlineIcon />}
              onClick={() => onTemplateChange(opt.value)}
              color={selectedTemplateKey === opt.value ? "primary" : "default"}
              variant={selectedTemplateKey === opt.value ? "filled" : "outlined"}
              sx={{ cursor: "pointer" }}
            />
          ))}
        </Stack>
      </Box>

      <SectionCard
        title={templateOptions.find((o) => o.value === selectedTemplateKey)?.label ?? "Email template"}
        description="Edit the subject, body, and active state for this email."
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  disabled={templateQuery.isLoading}
                />
              }
              label={
                <Typography variant="caption" color="text.secondary">
                  Active
                </Typography>
              }
              sx={{ mr: 0 }}
            />
          </Stack>
        }
      >
        {templateQuery.isLoading ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Loading template…</Typography>
          </Stack>
        ) : templateQuery.isError ? (
          <Alert severity="error">Failed to load email template.</Alert>
        ) : (
          <Stack spacing={2}>
            {/* Edit / Preview toggle */}
            <Tabs
              value={editorTab}
              onChange={(_, v) => setEditorTab(v)}
              sx={{
                minHeight: 36,
                borderBottom: "1px solid",
                borderColor: "divider",
                "& .MuiTab-root": { minHeight: 36, py: 0, textTransform: "none", fontSize: 13 },
              }}
            >
              <Tab value="edit" label="Edit" />
              <Tab value="preview" label="Preview" />
            </Tabs>

            {editorTab === "edit" && (
              <Stack spacing={2}>
                <TextField
                  label="Template name"
                  value={name}
                  size="small"
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Subject"
                  value={subject}
                  size="small"
                  onChange={(e) => setSubject(e.target.value)}
                  fullWidth
                  required
                />
                <TextField
                  label="Text body"
                  multiline
                  minRows={6}
                  value={textBody}
                  onChange={(e) => setTextBody(e.target.value)}
                  fullWidth
                  required
                  helperText="Plain-text version sent to email clients that don't render HTML."
                />
                <TextField
                  label="HTML body"
                  multiline
                  minRows={8}
                  value={htmlBody}
                  onChange={(e) => setHtmlBody(e.target.value)}
                  fullWidth
                  required
                  InputProps={{ sx: { fontFamily: "monospace", fontSize: 13 } }}
                />
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    bgcolor: "action.hover",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Available placeholders:{" "}
                    <Box component="code" sx={{ bgcolor: "background.paper", px: 0.5, borderRadius: 0.5 }}>
                      {"{{firstName}}"}
                    </Box>{" "}
                    <Box component="code" sx={{ bgcolor: "background.paper", px: 0.5, borderRadius: 0.5 }}>
                      {"{{email}}"}
                    </Box>
                  </Typography>
                </Box>
              </Stack>
            )}

            {editorTab === "preview" && (
              <Stack spacing={2}>
                <Alert severity="info" sx={{ py: 0.5 }}>
                  Previewing with: <strong>{PREVIEW_VALUES.firstName}</strong>, <strong>{PREVIEW_VALUES.email}</strong>
                </Alert>
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1.5,
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ px: 2, py: 1.5, bgcolor: "action.hover", borderBottom: "1px solid", borderColor: "divider" }}>
                    <Typography variant="caption" color="text.secondary" display="block">Subject</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {applyPreview(subject) || "(No subject)"}
                    </Typography>
                  </Box>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Text</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace", fontSize: 12 }}>
                      {applyPreview(textBody) || "(No text body)"}
                    </Typography>
                  </Box>
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>HTML</Typography>
                    <Box
                      sx={{ "& *": { maxWidth: "100%" } }}
                      dangerouslySetInnerHTML={{
                        __html: applyPreview(htmlBody) || "<p style='color:#999'>(No HTML body)</p>",
                      }}
                    />
                  </Box>
                </Box>
              </Stack>
            )}

            <Divider />

            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="contained"
                startIcon={
                  updateTemplateMutation.isPending ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <SaveOutlinedIcon />
                  )
                }
                disabled={updateTemplateMutation.isPending || !canSave}
                onClick={() =>
                  updateTemplateMutation.mutate({
                    name: name.trim(),
                    subject: subject.trim(),
                    html_body: htmlBody.trim(),
                    text_body: textBody.trim(),
                    is_active: isActive,
                  })
                }
              >
                Save template
              </Button>
              {updateTemplateMutation.isError && (
                <Alert severity="error" sx={{ py: 0.25, flex: 1 }}>Failed to save template.</Alert>
              )}
              {updateTemplateMutation.isSuccess && (
                <Alert severity="success" sx={{ py: 0.25, flex: 1 }}>Saved.</Alert>
              )}
            </Stack>
          </Stack>
        )}
      </SectionCard>
    </SettingsSection>
  );
};
